import { API } from './index'
import { AnyAction, Dispatch } from 'redux'
import { ChecklistInstance, PartialChecklistInstance } from '../../types/index'
import { logoutUser } from '@mv-submodules/inplant-coreadapter-fe/auth'
import * as Noty from 'noty'
import { CheckPrototype } from '@mv-submodules/inplant-checklist-fe-iblu/ui/components/views/ChecklistEdit/NewCheckModal'
import { ChecklistComponentFileData, EditComponentMetadataValue } from '../reducers/checklistEdit'
import { pickEditState } from '../../hook/useEditChecklist'

// ----------------- Checklist fetching actions -----------------
interface ChecklistEditIsFetchingAction {
  type: 'CHECKLIST_EDIT_IS_FETCHING'
}

interface ChecklistEditFetchSuccessAction {
  type: 'CHECKLIST_EDIT_FETCH_SUCCESS'
  payload: ChecklistInstance | PartialChecklistInstance | undefined
}

interface ChecklistEditFetchErrorAction {
  type: 'CHECKLIST_EDIT_FETCH_ERROR'
  payload: Error
}

const checklistEditIsFetching = (): ChecklistEditIsFetchingAction => {
  return {
    type: 'CHECKLIST_EDIT_IS_FETCHING',
  }
}

const checklistEditFetchSuccess = (
  checklist?: ChecklistInstance | PartialChecklistInstance
): ChecklistEditFetchSuccessAction => {
  return {
    payload: checklist,
    type: 'CHECKLIST_EDIT_FETCH_SUCCESS',
  }
}

const checklistEditFetchError = (error: Error): ChecklistEditFetchErrorAction => {
  return {
    payload: error,
    type: 'CHECKLIST_EDIT_FETCH_ERROR',
  }
}

// ----------------- Checklist submission actions -----------------
interface ChecklistEditIsSubmittingChecklist {
  type: 'CHECKLIST_EDIT_IS_SUBMITTING_CHECKLIST'
}

interface ChecklistEditSubmittedSuccessfully {
  type: 'CHECKLIST_EDIT_SUBMITTED_SUCCESSFULLY'
}

interface ChecklistEditSubmitChecklistError {
  type: 'CHECKLIST_EDIT_SUBMIT_CHECKLIST_ERROR'
  payload: string | undefined
}

const checklistIsSubmitting = (): ChecklistEditIsSubmittingChecklist => {
  return {
    type: 'CHECKLIST_EDIT_IS_SUBMITTING_CHECKLIST',
  }
}

const checklistSubmittedSuccessfully = (): ChecklistEditSubmittedSuccessfully => {
  return {
    type: 'CHECKLIST_EDIT_SUBMITTED_SUCCESSFULLY',
  }
}

const checklistSubmitChecklistError = (error: string | undefined): ChecklistEditSubmitChecklistError => {
  return {
    payload: error,
    type: 'CHECKLIST_EDIT_SUBMIT_CHECKLIST_ERROR',
  }
}

// ----------------- Checklist component actions -----------------
interface ChecklistEditIsModifiedAction {
  type: 'CHECKLIST_EDIT_IS_MODIFIED'
  payload: boolean
}

interface ChecklistEditIsUpdatingComponentAction {
  type: 'CHECKLIST_EDIT_IS_UPDATING_COMPONENT'
  payload: string
}

interface ChecklistEditSetComponentBlurredAction {
  type: 'CHECKLIST_EDIT_SET_COMPONENT_BLURRED'
  payload: string
}

interface ChecklistEditSetComponentErrorAction {
  type: 'CHECKLIST_EDIT_SET_COMPONENT_ERROR'
  payload: {
    componentId: string
    error?: string
  }
}

interface ChecklistEditSetComponentUpdatedAction {
  type: 'CHECKLIST_EDIT_SET_COMPONENT_UPDATED'
  payload: {
    componentId: string
    value: any
  }
}

interface ChecklistEditShowComponentsErrorsAction {
  type: 'CHECKLIST_EDIT_SHOW_COMPONENTS_ERRORS'
}

interface PersistRehydrateAction {
  type: 'persist/REHYDRATE'
}

interface ChecklistEditSetMultiFileComponentUpdatedAction {
  type: 'CHECKLIST_EDIT_SET_MULTI_FILE_COMPONENT_UPDATED'
  payload: { componentId: string; filesAdded: ChecklistComponentFileData[] }
}

interface ChecklistEditSetMultiFileComponentFileDeletedAction {
  type: 'CHECKLIST_EDIT_SET_MULTI_FILE_COMPONENT_FILE_DELETED'
  payload: { componentId: string; fileDeleted: string }
}

const isModified = (modified: boolean): ChecklistEditIsModifiedAction => {
  return {
    payload: modified,
    type: 'CHECKLIST_EDIT_IS_MODIFIED',
  }
}

const isUpdatingComponent = (componentId: string): ChecklistEditIsUpdatingComponentAction => {
  return {
    payload: componentId,
    type: 'CHECKLIST_EDIT_IS_UPDATING_COMPONENT',
  }
}

export const setComponentBlurred = (componentId: string): ChecklistEditSetComponentBlurredAction => {
  return {
    payload: componentId,
    type: 'CHECKLIST_EDIT_SET_COMPONENT_BLURRED',
  }
}

const setComponentError = (componentId: string, error?: string): ChecklistEditSetComponentErrorAction => {
  return {
    payload: { componentId, error },
    type: 'CHECKLIST_EDIT_SET_COMPONENT_ERROR',
  }
}

const setComponentUpdated = (
  componentId: string,
  value: EditComponentMetadataValue
): ChecklistEditSetComponentUpdatedAction => {
  return {
    payload: { componentId, value },
    type: 'CHECKLIST_EDIT_SET_COMPONENT_UPDATED',
  }
}

const setMultiFileComponentUpdated = (
  componentId: string,
  filesAdded: ChecklistComponentFileData[]
): ChecklistEditSetMultiFileComponentUpdatedAction => {
  return {
    payload: { componentId, filesAdded },
    type: 'CHECKLIST_EDIT_SET_MULTI_FILE_COMPONENT_UPDATED',
  }
}

const setMultiFileComponentFileDeleted = (
  componentId: string,
  fileDeleted: string
): ChecklistEditSetMultiFileComponentFileDeletedAction => {
  return {
    payload: { componentId, fileDeleted },
    type: 'CHECKLIST_EDIT_SET_MULTI_FILE_COMPONENT_FILE_DELETED',
  }
}

export const showComponentsErrors = (): ChecklistEditShowComponentsErrorsAction => {
  return {
    type: 'CHECKLIST_EDIT_SHOW_COMPONENTS_ERRORS',
  }
}

/**
 * Fetch the checklist data from the server.
 *
 * @param id - The checklist model id.
 */
export const checklistEditFetchChecklist = (
  id: string
): ((dispatch: Dispatch<AnyAction>) => Promise<string | null>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(checklistEditIsFetching())
    try {
      const response = await API().request(`/checklist/data/${id}`)
      const checklist = new ChecklistInstance(response)
      dispatch(checklistEditFetchSuccess(checklist))
      return checklist.id
    } catch (error) {
      _handleEditFetchError(error, dispatch)
      return null
    }
  }
}

/**
 * {@link checklistInstanceId} is the id of the checklist instance.
 *
 * If {@link componentId} is provided, the component will be updated with the new {@link value}.
 *
 * Set {@link conclude} to true to conclude the checklist.
 *
 */
export const checklistUpdateComponent = (
  checklistInstanceId: string,
  componentId: string | undefined,
  value: Array<{ subComponentName: string; value: number | number[] }> | string | number | undefined | null,
  conclude: boolean
): ((dispatch: Dispatch<AnyAction>) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(isModified(true))

    const componentUpdate = componentId ? { componentId, value } : undefined

    if (componentId) {
      dispatch(isUpdatingComponent(componentId))
    }

    if (conclude) {
      dispatch(checklistIsSubmitting())
    }

    try {
      await API()
        .request(`/checklist/update/${checklistInstanceId}`, {
          headers: {
            'content-type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({
            updates: componentUpdate ? [componentUpdate] : [],
            conclude,
          }),
        })
        .then(() => {
          if (componentId) {
            dispatch(setComponentUpdated(componentId, value))
          }
          if (conclude) {
            dispatch(checklistSubmittedSuccessfully())
            _notificationConcludedChecklist()
          }
        })
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
      if (conclude) {
        dispatch(checklistSubmitChecklistError(_parseFormError(error)))
      }
    }
  }
}

/**
 * Upload a file to the checklist 'file' component.
 *
 * @param componentId - The id of the component to upload the file to.
 * @param file - The file to upload.
 * @param options - The options for the upload.
 */
export const checklistFileComponentUpload = (
  componentId: string,
  file: File,
  options?: { hideNotification?: boolean }
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any): Promise<void> => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'file') {
        return
      }

      dispatch(isUpdatingComponent(componentId))

      const response = await _uploadFile(componentId, file, options)

      dispatch(setComponentUpdated(componentId, { path: response.file, blob: response.blob }))

      return
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Get the uploaded file from the checklist 'file' component.
 *
 * @param componentId - The id of the component to get the uploaded file from.
 * @param options - The options for the get.
 */
export const checklistFileComponentGetUploaded = (
  componentId: string,
  options?: { preview: boolean }
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any) => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'file') {
        return
      }

      const value = component.value
      if (!value?.path) {
        return
      }

      dispatch(isUpdatingComponent(componentId))

      const blob = await _getFile(value.path, options)

      dispatch(setComponentUpdated(componentId, { path: value.path, blob }))
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Upload multiple files to the checklist 'multiFile' component.
 *
 * @param componentId - The id of the component to upload the files to.
 * @param files - The files to upload.
 * @param options - The options for the upload.
 */
export const checklistMultiFileComponentUploads = (
  componentId: string,
  files: File[],
  options?: { hideNotification?: boolean }
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any): Promise<void> => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'multiFile') {
        return
      }

      dispatch(isUpdatingComponent(componentId))

      const responses = await Promise.all(files.map(file => _uploadFile(componentId, file, options)))

      dispatch(
        setMultiFileComponentUpdated(
          componentId,
          responses.map(response => ({ path: response.file, blob: response.blob }))
        )
      )

      return
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Get the uploaded files from the checklist 'multiFile' component.
 *
 * @param componentId - The id of the component to get the uploaded files from.
 * @param options - The options for the get.
 */
export const checklistMultiFileGetUploaded = (
  componentId: string,
  options?: { preview?: boolean }
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any) => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'multiFile') {
        return
      }

      const value = component.value

      if (!Array.isArray(value)) {
        return
      }

      dispatch(isUpdatingComponent(componentId))
      const filesAdded: ChecklistComponentFileData[] = []
      for (const fileObject of value) {
        const fileBlob = await _getFile(fileObject.path, options)
        filesAdded.push({ path: fileObject.path, blob: fileBlob })
      }

      dispatch(setComponentUpdated(componentId, filesAdded))
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Delete a file from the checklist 'multiFile' component.
 *
 * @param componentId - The id of the component to delete the file from.
 * @param filePath - The path of the file to delete.
 */
export const checklistMultiFileDeleteFile = (
  componentId: string,
  filePath: string
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any) => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'multiFile') {
        return
      }

      dispatch(isUpdatingComponent(componentId))

      await API().request(`/file/${filePath}`, {
        method: 'DELETE',
      })

      dispatch(setMultiFileComponentFileDeleted(componentId, filePath))
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Get a file from the checklist.
 *
 * @param filePath - The path of the file to get.
 */
export const checklistGetFileHighResolution = (
  filePath: string
): ((dispatch: Dispatch<AnyAction>) => Promise<Blob | null>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    try {
      return await _getFile(filePath, { preview: false })
    } catch (error) {
      _handleError(error, dispatch)
      return null
    }
  }
}

/**
 * Go to the next step of the checklist.
 *
 * @param id - The id of the checklist.
 */
export const checklistNextStep = (id: string): ((dispatch: Dispatch<AnyAction>) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(checklistIsSubmitting())
    try {
      await API().request(`/checklist/next/${id}`, {
        method: 'POST',
      })
      dispatch(checklistSubmittedSuccessfully())
    } catch (error) {
      _handleEditFetchError(error, dispatch)
      dispatch(checklistSubmitChecklistError(_parseFormError(error)))
    }
  }
}

export const addCheck = (
  sectionId: string,
  checkPrototype: CheckPrototype
): ((dispatch: Dispatch<AnyAction>) => Promise<ChecklistInstance>) => {
  return async dispatch => {
    try {
      const response = await API().request(`/checklist/add-check/${sectionId}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(checkPrototype),
      })
      return response
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

export const deleteCheck = (checkId: string): ((dispatch: Dispatch<AnyAction>) => Promise<void>) => {
  return async dispatch => {
    try {
      const response = await API().request(`/checklist/delete-check/${checkId}`, {
        method: 'DELETE',
      })
      return response
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

export const printChecklist = (checklistId: string): ((dispatch: Dispatch<AnyAction>) => Promise<Blob | null>) => {
  return async dispatch => {
    try {
      const blobData = await API().request(`/checklist/print/${checklistId}`)
      return blobData
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

export const exportCsvChecklist = (checklistId: string): ((dispatch: Dispatch<AnyAction>) => Promise<Blob | null>) => {
  return async dispatch => {
    try {
      const data = await API().request(`/checklist/csv/${checklistId}`)
      return new Blob([data], { type: 'application/octet-stream' })
    } catch (error) {
      _handleError(error, dispatch)
      return null
    }
  }
}

export const deleteChecklist = (checklistId: string) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(checklistEditIsFetching())
    try {
      await API().request(`/checklist/${checklistId}`, {
        method: 'DELETE',
      })
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

async function _uploadFile(
  filePath: string,
  file: Blob,
  options?: { hideNotification?: boolean }
): Promise<{ file: string; blob: Blob }> {
  const formData = new FormData()
  // const fileResized = await resizeImage({ file, maxSize: 1500 })
  formData.append('file', file)

  const response: { file: string } = await API().request(
    `/component/file/${filePath}`,
    {
      method: 'POST',
      body: formData,
    },
    false,
    options?.hideNotification
  )

  return { file: response.file, blob: file }
}

function _getFile(filePath: string, options?: { preview?: boolean }): Promise<Blob> {
  const qs = options && options.preview ? '?w=640&h=480' : ''
  return API().request(`/file/${filePath}${qs}`)
}

/**
 * Handle errors that are not related to the checklist edit.
 */
function _handleError(error: any, dispatch: Dispatch<AnyAction>) {
  if (error.name === 'FetchError' && error.statusCode === 401) {
    dispatch(logoutUser())
  }
  return null
}

/**
 * Call {@link _handleError} and handle errors that are related to the checklist edit.
 */
function _handleEditFetchError(error: any, dispatch: Dispatch<AnyAction>) {
  _handleError(error, dispatch)
  dispatch(checklistEditFetchError(new Error(error)))
}

/**
 * Call {@link _handleError} and handle errors that are related to the checklist component update.
 */
function _handleComponentUpdateError(error: unknown, dispatch: Dispatch<AnyAction>, componentId: string | undefined) {
  _handleError(error, dispatch)

  if (!componentId) {
    return
  }

  dispatch(setComponentError(componentId, _parseFormError(error, componentId)))
}

/**
 * Parse the error message from the form error.
 *
 * If {@link componentId} is provided, the error message will be parsed from the component error.
 */
function _parseFormError(error: unknown, componentId?: string): string | undefined {
  let errorMessage: string | undefined

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: string }).message === 'string'
  ) {
    errorMessage = (error as { message: string }).message
  }

  if (!componentId) {
    return errorMessage
  }

  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (error as { errors: object }).errors
    if (errors && typeof errors === 'object' && componentId in errors) {
      const componentErrors = (errors as Record<string, unknown>)[componentId]

      if (Array.isArray(componentErrors) && componentErrors.length > 0) {
        errorMessage = componentErrors.toString()
      }

      if (typeof componentErrors === 'string') {
        errorMessage = componentErrors
      }
    }
  }

  return errorMessage
}

function _notificationConcludedChecklist() {
  new Noty({
    text: `
    <div>
      <strong>Operazione completata</strong>
      <br>
      <span>Checklist conclusa con successo</span>
    </div>`,
    type: 'success',
    theme: 'bootstrap-v4',
    layout: 'topRight',
    id: 'fetch-wrapper-noty-container',
    timeout: 5000,
    closeWith: ['click', 'button'],
  }).show()
}

export type ChecklistEditAction =
  | ChecklistEditIsFetchingAction
  | ChecklistEditFetchSuccessAction
  | ChecklistEditFetchErrorAction
  | ChecklistEditIsModifiedAction
  | ChecklistEditIsSubmittingChecklist
  | ChecklistEditSubmittedSuccessfully
  | ChecklistEditSubmitChecklistError
  | ChecklistEditIsUpdatingComponentAction
  | ChecklistEditSetComponentBlurredAction
  | ChecklistEditSetComponentErrorAction
  | ChecklistEditSetComponentUpdatedAction
  | ChecklistEditSetMultiFileComponentUpdatedAction
  | ChecklistEditSetMultiFileComponentFileDeletedAction
  | ChecklistEditShowComponentsErrorsAction
  | PersistRehydrateAction
